import { useEffect, useState } from 'react';
import { X, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { dashboardApi, type TechnicianDetail } from '@/api/dashboardApi';
import { workOrdersApi } from '@/api/workOrdersApi';
import Toast from '@/components/ui/Toast';

interface TechnicianUtilizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TechnicianUtilizationModal({ isOpen, onClose }: TechnicianUtilizationModalProps) {
  const [technicians, setTechnicians] = useState<TechnicianDetail[]>([]);
  const [selectedTech, setSelectedTech] = useState<TechnicianDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [reassigning, setReassigning] = useState<number | null>(null);
  const [showReassignModal, setShowReassignModal] = useState<number | null>(null);
  const [selectedNewTech, setSelectedNewTech] = useState<number | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [pausingId, setPausingId] = useState<number | null>(null);
  const [freeingUpId, setFreeingUpId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadTechnicianWorkload();
    }
  }, [isOpen]);

  async function loadTechnicianWorkload() {
    setLoading(true);
    try {
      const response = await dashboardApi.getTechnicianWorkload();
      console.log('Technician workload response:', response);
      setTechnicians(response.technicians || []);
      if (response.technicians?.length > 0) {
        setSelectedTech(response.technicians[0]);
      } else {
        console.warn('No technicians in response');
      }
    } catch (error) {
      console.error('Failed to load technician workload:', error);
      setToastMessage('Failed to load technician data');
    } finally {
      setLoading(false);
    }
  }

  async function handleReassign(workOrderId: number, newTechId: number) {
    if (newTechId <= 0) {
      setToastMessage('Please select a technician');
      return;
    }

    setReassigning(workOrderId);
    try {
      await workOrdersApi.assignTechnician(workOrderId, newTechId);
      setToastMessage('Work order reassigned successfully');
      setShowReassignModal(null);
      setSelectedNewTech(null);
      await loadTechnicianWorkload();
    } catch (error) {
      console.error('Failed to reassign:', error);
      setToastMessage('Failed to reassign work order');
    } finally {
      setReassigning(null);
    }
  }

  async function handlePause(workOrderId: number) {
    setPausingId(workOrderId);
    try {
      await workOrdersApi.updateStatus(workOrderId, 'OnHold');
      setToastMessage('Work order put on hold');
      await loadTechnicianWorkload();
    } catch (error) {
      console.error('Failed to pause work order:', error);
      setToastMessage('Failed to put work order on hold');
    } finally {
      setPausingId(null);
    }
  }

  async function handleFreeUp(workOrderId: number) {
    setFreeingUpId(workOrderId);
    try {
      await workOrdersApi.assignTechnician(workOrderId, null);
      setToastMessage('Technician freed up from job');
      await loadTechnicianWorkload();
    } catch (error) {
      console.error('Failed to free up technician:', error);
      setToastMessage('Failed to free up technician');
    } finally {
      setFreeingUpId(null);
    }
  }

  if (!isOpen) return null;

  const getCapacityColor = (status: string) => {
    switch (status) {
      case 'Normal':
        return 'text-green-600 bg-green-50';
      case 'AtLimit':
        return 'text-amber-600 bg-amber-50';
      case 'OverLimit':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-slate-600 bg-slate-50';
    }
  };

  const getCapacityIcon = (status: string) => {
    switch (status) {
      case 'Normal':
        return <CheckCircle className="h-4 w-4" />;
      case 'AtLimit':
        return <Clock className="h-4 w-4" />;
      case 'OverLimit':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-xl font-bold">Technician Workload</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Technician List */}
          <div className="w-80 border-r border-slate-200 overflow-y-auto bg-slate-50">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin w-6 h-6 border-3 border-primary border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {technicians.map((tech) => (
                  <button
                    key={tech.staffId}
                    onClick={() => setSelectedTech(tech)}
                    className={`w-full text-left px-4 py-4 transition-colors ${
                      selectedTech?.staffId === tech.staffId
                        ? 'bg-primary text-white'
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-900'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-sm">{tech.fullName}</p>
                        <p className={`text-xs mt-1 px-2 py-1 rounded inline-flex items-center gap-1 ${getCapacityColor(tech.capacityStatus)}`}>
                          {getCapacityIcon(tech.capacityStatus)}
                          {tech.currentJobCount}/{tech.jobLimit} jobs ({Math.round(tech.capacityPercentage)}%)
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel - Job Details */}
          <div className="flex-1 overflow-y-auto p-6">
            {selectedTech ? (
              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-lg mb-2">{selectedTech.fullName}</h3>
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${getCapacityColor(selectedTech.capacityStatus)}`}>
                    {getCapacityIcon(selectedTech.capacityStatus)}
                    <span className="text-sm font-medium">
                      {selectedTech.currentJobCount}/{selectedTech.jobLimit} jobs ({Math.round(selectedTech.capacityPercentage)}%)
                    </span>
                  </div>
                </div>

                {selectedTech.assignedJobs.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <p className="text-sm">No active assignments</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedTech.assignedJobs.map((job) => (
                      <div
                        key={job.workOrderId}
                        className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-sm">#{job.workOrderNumber}</p>
                            <p className="text-xs text-slate-600 mt-0.5">{job.vehicle}</p>
                          </div>
                          <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded">
                            {job.status}
                          </span>
                        </div>

                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowReassignModal(job.workOrderId)}
                            className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                          >
                            Reassign
                          </button>
                          <button
                            onClick={() => handlePause(job.workOrderId)}
                            disabled={pausingId === job.workOrderId}
                            className="text-xs font-medium text-amber-600 hover:text-amber-700 disabled:opacity-50 transition-colors"
                          >
                            {pausingId === job.workOrderId ? 'Pausing...' : 'Pause'}
                          </button>
                          <button
                            onClick={() => handleFreeUp(job.workOrderId)}
                            disabled={freeingUpId === job.workOrderId}
                            className="text-xs font-medium text-slate-600 hover:text-slate-800 disabled:opacity-50 transition-colors"
                          >
                            {freeingUpId === job.workOrderId ? 'Freeing up...' : 'Free Up'}
                          </button>
                        </div>

                        {/* Reassign Sub-modal */}
                        {showReassignModal === job.workOrderId && (
                          <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                            <label className="text-xs font-medium text-slate-700 block mb-2">
                              Assign to:
                            </label>
                            <div className="space-y-2">
                              <select
                                value={selectedNewTech || ''}
                                onChange={(e) => setSelectedNewTech(Number(e.target.value))}
                                className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                              >
                                <option value="">-- Select technician --</option>
                                {technicians
                                  .filter((t) => t.staffId !== selectedTech.staffId)
                                  .map((t) => (
                                    <option key={t.staffId} value={t.staffId}>
                                      {t.fullName} ({t.currentJobCount}/{t.jobLimit})
                                    </option>
                                  ))}
                              </select>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleReassign(job.workOrderId, selectedNewTech || 0)}
                                  disabled={!selectedNewTech || reassigning === job.workOrderId}
                                  className="flex-1 px-2 py-1 bg-primary text-white text-xs font-medium rounded hover:bg-primary/90 disabled:opacity-50 transition-colors"
                                >
                                  {reassigning === job.workOrderId ? 'Reassigning...' : 'Confirm'}
                                </button>
                                <button
                                  onClick={() => {
                                    setShowReassignModal(null);
                                    setSelectedNewTech(null);
                                  }}
                                  className="flex-1 px-2 py-1 bg-slate-200 text-slate-700 text-xs font-medium rounded hover:bg-slate-300 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                <p className="text-sm">Select a technician to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage(null)}
          type="success"
          duration={3000}
        />
      )}
    </div>
  );
}
